
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Trash2, ToggleLeft, ToggleRight, Play, Loader2 } from 'lucide-react';

const VideoList = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchVideos = async () => {
        try {
            setLoading(true);
            const response = await api.get('/videos');
            setVideos(response);
            setError('');
        } catch (err) {
            console.error(err);
            setError('Failed to load media.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVideos();
    }, []);

    const toggleFeatured = async (video) => {
        try {
            const updatedVideo = await api.patch(`/videos/${video._id}`, {
                isFeatured: !video.isFeatured
            });
            setVideos(videos.map(v => v._id === video._id ? updatedVideo : v));
        } catch (err) {
            console.error(err);
            setError('Failed to update media status.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this media?')) return;
        try {
            await api.delete(`/videos/${id}`);
            setVideos(videos.filter(v => v._id !== id));
        } catch (err) {
            console.error(err);
            setError('Failed to delete media.');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
        );
    }

    if (videos.length === 0) {
        return (
            <div className="text-center p-8 text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                No media uploaded yet.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-800">Manage Media</h3>

            {error && (
                <div className="p-4 bg-red-50 text-red-800 rounded-lg text-sm mb-4">
                    {error}
                </div>
            )}

            <div className="grid gap-4">
                {videos.map((video) => (
                    <div key={video._id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-center gap-4 transition-all hover:shadow-md">
                        {/* Video Thumbnail / Preview */}
                        <div className="relative w-full md:w-48 aspect-video bg-black rounded-lg overflow-hidden flex-shrink-0 group">
                            {video.url && video.url.match(/\.(mp4|webm|ogg)$/i) ? (
                                <video
                                    src={video.url}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <img
                                    src={video.url}
                                    alt={video.title}
                                    className="w-full h-full object-cover"
                                />
                            )}
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <Play className="w-8 h-8 text-white fill-white" />
                            </div>
                        </div>

                        {/* Video Info */}
                        <div className="flex-1 min-w-0 w-full text-center md:text-left">
                            <h4 className="font-semibold text-gray-900 truncate">{video.title}</h4>
                            <div className="flex items-center justify-center md:justify-start gap-2 mt-1">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 capitalize">
                                    {video.sizeCategory || 'Desktop'}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {new Date(video.createdAt).toLocaleDateString()}
                                </span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3 w-full md:w-auto justify-center md:justify-end border-t md:border-t-0 pt-4 md:pt-0 mt-2 md:mt-0">
                            <button
                                onClick={() => toggleFeatured(video)}
                                className={`
                                    flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                                    ${video.isFeatured
                                        ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'}
                                `}
                                title={video.isFeatured ? "Currently visible on Home Page" : "Hidden from Home Page"}
                            >
                                {video.isFeatured ? (
                                    <>
                                        <ToggleRight className="w-5 h-5" />
                                        <span>Featured</span>
                                    </>
                                ) : (
                                    <>
                                        <ToggleLeft className="w-5 h-5" />
                                        <span>Hidden</span>
                                    </>
                                )}
                            </button>

                            <button
                                onClick={() => handleDelete(video._id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Media"
                            >
                                <Trash2 className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default VideoList;
